// src/components/froggerAI/useFroggerNavigate.js
//
// WHAT THE DOCK DOES WITH A `navigate` CHUNK (I-03 R6 · S3-06.3).
//
// The backend hands back `{kind, id, name, route}` — the route built in
// ONE place (`frogger_ai/handlers_navigate._route_for`) so the dock, the
// standalone page and anything later cannot disagree about what "open
// rack 3" means.
//
// ⚠️ THE ROUTE IS STILL CHECKED HERE. It arrives on a stream, and a
// react-router `navigate()` will happily follow any string it is given —
// so a route that does not begin `/facilities/<this facility>/` is
// rebuilt from the kind and the id rather than followed. That is not
// paranoia about our own server; it is that this is the one place in the
// app where a model's output reaches the router, and "it came from the
// backend" stops being a guarantee the moment anything upstream is
// prompt-influenced.
//
// Every destination below is a deep link that already existed, except
// `?highlight=` which this sprint adds to the racks page:
//
//   animal    -> /facilities/:id/animals?animal=<id>     (row expanded)
//   rack      -> /facilities/:id/racks-enclosures/<id>   (RackDetail)
//   enclosure -> /facilities/:id/racks-enclosures?enclosure=<id>
//                (the board redirects to the tank's rack and opens it)
//   clutch    -> /facilities/:id/clutch?clutch=<id>      (S3-03.2)
//   highlight -> the room map, or — for a SHELVED tank, which the room
//                map does not draw — that tank's RACK with the tank lit
//                (`.../racks-enclosures/<rackId>?highlight=<tankId>`).
//                ON A PHONE there is no room map at all, so a lit RACK
//                goes to its own detail route and a lit FREESTANDING
//                tank goes to its ROOM's list: see `phoneRouteFor`.
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsPhone from '../../hooks/useIsPhone';

/** The route this kind + id means, built client-side. */
export const routeFor = (facilityId, to) => {
  if (!facilityId || !to || !to.id) return null;
  const base = `/facilities/${facilityId}`;
  const id = encodeURIComponent(to.id);
  // A HIGHLIGHT is a different destination from an OPEN, and for a
  // shelved tank it is a different PAGE: the room map draws racks and
  // freestanding cards, so lighting a tank that lives on a shelf has to
  // happen on its rack's own detail route. `rack_id` on the payload is
  // what says which — see `handlers_navigate._route_for`.
  if (to.highlight) {
    if (to.kind === 'enclosure' && to.rack_id) {
      return `${base}/racks-enclosures/${encodeURIComponent(to.rack_id)}?highlight=${id}`;
    }
    return `${base}/racks-enclosures?highlight=${id}`;
  }
  switch (to.kind) {
    case 'animal': return `${base}/animals?animal=${id}`;
    case 'rack': return `${base}/racks-enclosures/${id}`;
    case 'enclosure': return `${base}/racks-enclosures?enclosure=${id}`;
    case 'clutch': return `${base}/clutch?clutch=${id}`;
    case 'room': return `${base}/racks-enclosures/rooms/${id}`;
    default: return null;
  }
};

/**
 * THE DEVICE DECIDES WHERE A LIT RACK LANDS — and only the device can.
 *
 * `_route_for` on the server has no viewport, so `showOnMap <rack>`
 * builds the room map (`racks-enclosures?highlight=<rackId>`). On a
 * DESKTOP that is right: the map draws the rack tile and lights it.
 * On a PHONE there is no map. `RacksBoard`'s `isPhone` branch renders
 * three different views, and that URL — no rackId in the path and no
 * roomId either — is the ROOM CHOOSER: "Which room?". The keeper was
 * handed a chooser, nothing was lit, and the rack they asked for was
 * two taps away (operator phone eye test, 2026-09-12).
 *
 * So on a phone a lit RACK goes to the rack's OWN detail route, which
 * is a first-class phone view (`phone/PhoneRackDetail`) — the rack IS
 * the screen, shelves and tanks already drawn — and it keeps the
 * `?highlight=` so the board still lights it and scrolls to it.
 *
 * ⚠️ This is the RACKS BOARD's phone rule (`hooks/useIsPhone`), NOT the
 * dock's. The dock switches on width >= 1024 alone; the board switches
 * on `(max-width: 768px), (pointer: coarse) and (max-width: 1024px)`,
 * because a drag-and-drop floor plan is about the finger. An iPad Mini
 * in landscape is exactly 1024 and gets the DESKTOP dock and the PHONE
 * board at the same time — inferring one from the other is wrong there.
 *
 * A SHELVED lit tank needs nothing here: it already routes to its rack's
 * detail page (`rack_id`), which is that same first-class phone view.
 *
 * A FREESTANDING lit tank is the third case, and it has its own screen
 * too. It is not on a rack, so the rack re-aim cannot carry it — but it
 * IS one of the Elsewhere zone's cards, and the phone draws that zone on
 * the ROOM route (`racks-enclosures/rooms/:roomId` — `RacksBoard`'s "ONE
 * ROOM, as a list", which hands `RoomList` the room's `freestanding`).
 * `?highlight=` works there exactly as it does on the desktop map: the
 * board sets `revealedTankId`, and `RoomList`'s `revealedTankId` effect
 * (its own, not the desktop zone's) EXPANDS that tank's card and scrolls
 * it into view. The phone list draws every freestanding tank — the
 * 12-card cap and its reveal guard are the DESKTOP Elsewhere zone's, and
 * there is nothing here for a reveal to have to open past. So the tank
 * goes to ITS ROOM, lit. `room_id` on the payload is what says which room — see
 * `handlers_navigate._room_id_of`.
 *
 * ⚠️ A ROOM-LESS freestanding tank STILL falls through to the server's
 * room-map route, i.e. to the chooser. Per CLAUDE.md such a tank is
 * drawn with the MAIN room, and which room that is is not something this
 * payload says; picking one here would light a card in a room the tank
 * is not filed in. The known gap is now exactly that one case.
 *
 * Returns null when this is not a case the phone re-aims.
 */
export const phoneRouteFor = (facilityId, to) => {
  if (!facilityId || !to || !to.id) return null;
  const id = encodeURIComponent(to.id);
  // ── A SUPPLY (review of the S3-06.4 Cowork pass, F-7) ──────────────
  //
  // `_thing_on_the_map` lights the nearest thing that IS DRAWN — the
  // tank, else the rack — so a fogger on rack 2 arrives as `kind:
  // 'thing'` carrying a room-MAP route. On a phone that route is the
  // room chooser, the same failure a lit rack had: the keeper asked
  // where the fogger is and was handed "Which room?".
  //
  // Checked BEFORE the `highlight` gate, because the third case — a
  // supply with no rack and no tank — carries `highlight: false` and its
  // own `?thing=` room route, and that one is already right on a phone.
  // The rack and the freestanding-tank cases both land on a screen that
  // now DRAWS supplies: the rack panel this same review added, and the
  // room list's panel that was already there.
  if (to.kind === 'thing') {
    const base = `/facilities/${facilityId}/racks-enclosures`;
    if (to.rack_id) {
      return `${base}/${encodeURIComponent(to.rack_id)}?thing=${id}`;
    }
    if (to.room_id) {
      return `${base}/rooms/${encodeURIComponent(to.room_id)}?thing=${id}`;
    }
    return null;
  }
  if (!to.highlight) return null;
  if (to.kind === 'rack') {
    return `/facilities/${facilityId}/racks-enclosures/${id}?highlight=${id}`;
  }
  // Freestanding (no `rack_id`) AND filed in a room. A shelved tank keeps
  // the rack route the server already built for it.
  if (to.kind === 'enclosure' && !to.rack_id && to.room_id) {
    const roomId = encodeURIComponent(to.room_id);
    return `/facilities/${facilityId}/racks-enclosures/rooms/${roomId}`
      + `?highlight=${id}`;
  }
  return null;
};

/**
 * The route to actually follow: the phone's when the device re-aims
 * this one, the server's when it belongs to this facility, ours
 * otherwise, and nothing at all when none of them resolves.
 *
 * The phone override is computed from `kind` + `id` (+ `rack_id` /
 * `room_id`) ALONE and never from `to.route`, so nothing on the wire can
 * steer it anywhere but a route of THIS facility — and the prefix
 * check below is untouched for everything else: a route that does not
 * begin `/facilities/<this facility>/` is still rebuilt, not followed.
 */
export const safeRoute = (facilityId, to, { isPhone = false } = {}) => {
  if (!facilityId || !to) return null;
  if (isPhone) {
    const phoned = phoneRouteFor(facilityId, to);
    if (phoned) return phoned;
  }
  const prefix = `/facilities/${facilityId}/`;
  const given = typeof to.route === 'string' ? to.route : '';
  // `startsWith` on the FULL prefix including the trailing slash, so
  // `/facilities/<other-facility-id>...` cannot pass by sharing a
  // leading substring of ours.
  if (given.startsWith(prefix)) return given;
  return routeFor(facilityId, to);
};

export default function useFroggerNavigate(facilityId) {
  const navigate = useNavigate();
  // The racks board's own rule, read HERE — where the route is decided
  // — rather than inferred from the dock's layout.
  const isPhone = useIsPhone();
  return useCallback((to) => {
    const route = safeRoute(facilityId, to, { isPhone });
    if (!route) {
      console.warn('[FroggerAI] navigate ignored - no route for', to);
      return;
    }
    // A PUSH, not a replace: "show me rack 3" is somewhere the operator
    // chose to go, and Back has to take them off it.
    navigate(route);
  }, [facilityId, isPhone, navigate]);
}
